/**
 * Écran « Règles » : les règles de classement par priorité. Une règle qui
 * correspond classe l'écriture avant que Jev ne soit sollicité. L'administration
 * crée, réordonne, désactive et supprime ; chaque règle se teste avant d'être
 * enregistrée.
 */

import { dateFr, entier, eurosCentimes, horodatageFr } from '@scc/core';
import { ErreurApi, type Identite } from '../../api.js';
import { enteteSection, noteLecture } from '../../composants.js';
import { h, vider } from '../../dom.js';
import {
  attendreFinDePasse,
  creerRegle,
  listeRegles,
  listeSousPostes,
  modifierRegle,
  phraseBilan,
  supprimerRegle,
  surFinDePasse,
  testerRegle,
  type ChampRegle,
  type OperateurRegle,
  type RegleServie,
  type Sens,
  type SousPoste,
} from './api.js';
import { libellePosteOuHorsBudget } from '../../postes.js';
import { comparerSousPostes, grouperSousPostesParPoste, libelleCompte } from './comptes.js';
import { prioritesApresDeplacement } from './ordre-regles.js';
import { avecVerrou, type Verrouillable } from './verrou.js';

const CHAMPS: Readonly<Record<ChampRegle, string>> = {
  tiers: 'tiers',
  libelle: 'libellé',
  compte: 'compte',
  compte_libelle: 'libellé du compte',
  type_transaction: 'type d’opération',
};

const OPERATEURS: Readonly<Record<OperateurRegle, string>> = {
  egal: 'est égal à',
  contient: 'contient',
  commence_par: 'commence par',
};

const SENS: Readonly<Record<Sens, string>> = {
  recette: 'Entrées',
  depense: 'Sorties',
  tous: 'Tous sens',
};

const ORIGINES: Readonly<Record<string, string>> = {
  initiale: 'Amorce',
  arbitrage: 'Arbitrage',
  manuelle: 'Manuelle',
};

/** Un sous-poste est une cible admise pour une règle du sens donné. */
const cibleAdmise = (sp: SousPoste, sens: Sens): boolean =>
  !sp.archive && (sp.sens === 'tous' || sp.sens === sens);

export function vueRegles(identite: Identite): HTMLElement {
  const admin = identite.role === 'administration';
  const corps = h('div', {}, h('p', { classe: 'note-lecture' }, 'Chargement des règles…'));
  const retour = h('div', { role: 'status' });
  const etatPasse = h('p', { classe: 'note-lecture', role: 'status' });
  const zoneTest = h('div', { role: 'status' });
  let sousPostes: SousPoste[] = [];
  let remplirFormulaire: (() => void) | null = null;
  /** Charge une règle dans le formulaire pour la modifier. */
  let editer: ((r: RegleServie) => void) | null = null;
  let generation = 0;

  const signaler = (texte: string, ok: boolean): void => {
    vider(retour);
    retour.appendChild(h('p', { classe: `retour-editeur ${ok ? 'ok' : 'ko'}`, role: ok ? null : 'alert' }, texte));
  };
  const echec = (e: unknown): void => {
    signaler(e instanceof ErreurApi ? e.message : 'Opération refusée.', false);
  };

  /** Après une modification : la liste est à jour tout de suite, le reclassement suit en arrière-plan. */
  const suivreReclassement = async (message: string): Promise<void> => {
    signaler(`${message} Reclassement des écritures en cours…`, true);
    etatPasse.textContent = 'Classement en cours…';
    try {
      const r = await attendreFinDePasse();
      etatPasse.textContent = phraseBilan(r.passe) ?? '';
      signaler(`${message} Reclassement terminé.`, true);
    } catch (e) {
      etatPasse.textContent = '';
      echec(e);
    }
  };

  /**
   * Exécute une modification, recharge la liste, puis suit le reclassement.
   * Les contrôles de la ligne sont verrouillés le temps de la requête.
   */
  const modifier = (
    message: string,
    controles: readonly Verrouillable[],
    action: () => Promise<unknown>,
  ): void => {
    void (async () => {
      try {
        await avecVerrou(controles, action);
      } catch (e) {
        echec(e);
        await charger();
        return;
      }
      await charger();
      await suivreReclassement(message);
    })();
  };

  const libelleCible = (id: string): string => {
    const sp = sousPostes.find((s) => s.id === id);
    return sp ? `${libelleCompte(sp)}${sp.archive ? ' (archivé)' : ''}` : id;
  };

  const critere = (r: RegleServie): string =>
    `${CHAMPS[r.champ]} ${OPERATEURS[r.operateur]} « ${r.valeur} »`;

  const ligne = (r: RegleServie, i: number, toutes: readonly RegleServie[]): HTMLElement => {
    const precedente = toutes[i - 1];
    const suivante = toutes[i + 1];
    /** Contrôles de la ligne : verrouillés ensemble pendant une requête. */
    const controlesLigne = (): (HTMLButtonElement | HTMLInputElement)[] => [
      ...tr.querySelectorAll<HTMLButtonElement | HTMLInputElement>('button, input'),
    ];
    const deplacer = (delta: -1 | 1): void => {
      modifier('Ordre des règles modifié.', controlesLigne(), async () => {
        // Deux requêtes pour un échange : entre les deux, l'ordre est transitoirement incohérent
        // (limite connue ; une coupure entre elles laisse deux priorités échangées à moitié).
        for (const m of prioritesApresDeplacement(toutes, i, delta)) await modifierRegle(m.id, { priorite: m.priorite });
      });
    };

    const actif = h('input', { type: 'checkbox', 'aria-label': `Règle ${critere(r)} active`, disabled: !admin });
    actif.checked = r.actif;
    actif.addEventListener('change', () => {
      modifier(actif.checked ? 'Règle activée.' : 'Règle désactivée.', controlesLigne(), () =>
        modifierRegle(r.id, { actif: actif.checked }),
      );
    });

    const tr = h(
      'tr',
      { classe: r.actif ? '' : 'regle-inactive' },
      h('td', { classe: 'num' }, String(r.priorite)),
      h('td', {}, critere(r)),
      h('td', {}, SENS[r.sens]),
      h('td', {}, libelleCible(r.sousPoste)),
      h('td', {}, ORIGINES[r.origine] ?? r.origine),
      h('td', { classe: 'num' }, entier(r.nbEcritures)),
      h('td', {}, r.creePar, h('div', { classe: 'note-lecture' }, horodatageFr(r.creeLe))),
      h('td', {}, actif),
      admin
        ? h(
            'td',
            {},
            h(
              'div',
              { classe: 'actions-arbitrage' },
              h(
                'button',
                {
                  classe: 'btn-action',
                  type: 'button',
                  'aria-label': `Monter la règle ${critere(r)}`,
                  disabled: !precedente,
                  surClick: () => {
                    deplacer(-1);
                  },
                },
                '↑',
              ),
              h(
                'button',
                {
                  classe: 'btn-action',
                  type: 'button',
                  'aria-label': `Descendre la règle ${critere(r)}`,
                  disabled: !suivante,
                  surClick: () => {
                    deplacer(1);
                  },
                },
                '↓',
              ),
              h(
                'button',
                {
                  classe: 'btn-action',
                  type: 'button',
                  'aria-label': `Modifier la règle ${critere(r)}`,
                  surClick: () => {
                    editer?.(r);
                  },
                },
                'Modifier',
              ),
              h(
                'button',
                {
                  classe: 'btn-action',
                  type: 'button',
                  'aria-label': `Supprimer la règle ${critere(r)}`,
                  surClick: () => {
                    if (!window.confirm(`Supprimer la règle « ${critere(r)} » ? Les écritures concernées seront reclassées.`)) return;
                    modifier('Règle supprimée.', controlesLigne(), () => supprimerRegle(r.id));
                  },
                },
                'Supprimer',
              ),
            ),
          )
        : null,
    );
    return tr;
  };

  const charger = async (): Promise<void> => {
    const moi = ++generation;
    try {
      const [{ regles }, sp] = await Promise.all([listeRegles(), listeSousPostes()]);
      if (moi !== generation) return;
      sousPostes = sp.sousPostes;
      remplirFormulaire?.();
      const triees = [...regles].sort((a, b) => a.priorite - b.priorite || a.id - b.id);
      vider(corps);
      if (triees.length === 0) {
        corps.appendChild(noteLecture('Aucune règle de classement.'));
        return;
      }
      corps.appendChild(
        h(
          'table',
          { classe: 'comp-table tableau-arbitrage' },
          h('caption', { classe: 'invisible' }, 'Règles de classement par priorité'),
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { scope: 'col', classe: 'num' }, 'Priorité'),
              h('th', { scope: 'col' }, 'Critère'),
              h('th', { scope: 'col' }, 'Sens'),
              h('th', { scope: 'col' }, 'Sous-poste'),
              h('th', { scope: 'col' }, 'Origine'),
              h('th', { scope: 'col', classe: 'num' }, 'Écritures'),
              h('th', { scope: 'col' }, 'Créée par'),
              h('th', { scope: 'col' }, 'Active'),
              admin ? h('th', { scope: 'col' }, 'Actions') : null,
            ),
          ),
          h('tbody', {}, ...triees.map(ligne)),
        ),
      );
    } catch (e) {
      if (moi !== generation) return;
      vider(corps);
      corps.appendChild(noteLecture('Les règles n’ont pas pu être chargées.'));
      echec(e);
    }
  };

  // ── Formulaire « Nouvelle règle » ───────────────────────────────────────
  const formulaire = (): HTMLElement => {
    const champ = h(
      'select',
      { id: 'regle-champ' },
      ...(Object.keys(CHAMPS) as ChampRegle[]).map((c) => h('option', { value: c }, CHAMPS[c])),
    );
    champ.value = 'libelle';
    const operateur = h(
      'select',
      { id: 'regle-operateur' },
      ...(Object.keys(OPERATEURS) as OperateurRegle[]).map((o) => h('option', { value: o }, OPERATEURS[o])),
    );
    operateur.value = 'contient';
    const valeur = h('input', { type: 'text', id: 'regle-valeur', maxlength: 200 });
    const sens = h(
      'select',
      { id: 'regle-sens' },
      ...(Object.keys(SENS) as Sens[]).map((s) => h('option', { value: s }, SENS[s])),
    );
    sens.value = 'depense';
    const cible = h('select', { id: 'regle-cible' });

    const remplirCibles = (): void => {
      const courant = cible.value;
      vider(cible);
      cible.appendChild(h('option', { value: '' }, 'Choisir…'));
      // Par poste (générique en tête, puis les comptes par code) : le plan comptable compte plus d'une centaine de cibles.
      for (const [poste, membres] of grouperSousPostesParPoste(sousPostes.filter((x) => cibleAdmise(x, sens.value as Sens)))) {
        cible.appendChild(
          h(
            'optgroup',
            { label: libellePosteOuHorsBudget(poste) },
            ...[...membres].sort(comparerSousPostes).map((s) => h('option', { value: s.id }, libelleCompte(s))),
          ),
        );
      }
      cible.value = courant;
    };
    sens.addEventListener('change', remplirCibles);
    // Les sous-postes arrivent avec la liste : `charger` remplit les cibles à son tour.
    remplirFormulaire = remplirCibles;
    remplirCibles();

    const saisie = (): { champ: ChampRegle; operateur: OperateurRegle; valeur: string; sens: Sens; sousPoste: string } | null => {
      if (valeur.value.trim() === '') {
        signaler('Indiquez la valeur du critère.', false);
        return null;
      }
      if (cible.value === '') {
        signaler('Choisissez le sous-poste visé.', false);
        return null;
      }
      return {
        champ: champ.value as ChampRegle,
        operateur: operateur.value as OperateurRegle,
        valeur: valeur.value.trim(),
        sens: sens.value as Sens,
        sousPoste: cible.value,
      };
    };

    /** Règle en cours de modification ; `null` : le formulaire crée une règle. */
    let enEdition: RegleServie | null = null;
    const titre = h('h3', {}, 'Nouvelle règle');
    const boutonTester = h('button', { classe: 'btn-action', type: 'button', surClick: () => { tester(); } }, 'Tester');
    const boutonEnregistrer = h('button', { classe: 'btn-action', type: 'button', surClick: () => { enregistrer(); } }, 'Enregistrer');
    const boutonAnnuler = h('button', { classe: 'btn-action', type: 'button', hidden: true, surClick: () => { quitterEdition(); } }, 'Annuler');
    const verrouillables = [boutonTester, boutonEnregistrer, boutonAnnuler, champ, operateur, valeur, sens, cible];

    const quitterEdition = (): void => {
      enEdition = null;
      titre.textContent = 'Nouvelle règle';
      boutonAnnuler.hidden = true;
      valeur.value = '';
      vider(zoneTest);
    };

    editer = (r) => {
      enEdition = r;
      titre.textContent = `Modifier la règle n° ${String(r.id)}`;
      boutonAnnuler.hidden = false;
      champ.value = r.champ;
      operateur.value = r.operateur;
      valeur.value = r.valeur;
      sens.value = r.sens;
      remplirCibles();
      // Une cible archivée n'est plus proposée : le serveur la refuserait.
      cible.value = r.sousPoste;
      vider(zoneTest);
      if (cible.value === '') signaler('Le sous-poste visé par cette règle est archivé : choisissez-en un autre.', false);
      carte.scrollIntoView({ block: 'nearest' });
      valeur.focus();
    };

    const tester = (): void => {
      const r = saisie();
      if (!r) return;
      void (async () => {
        try {
          const t = await avecVerrou(verrouillables, () => testerRegle(r));
          vider(zoneTest);
          zoneTest.appendChild(
            h(
              'div',
              { classe: 'card' },
              h('p', {}, h('strong', {}, `${entier(t.nb)} écriture(s) captée(s)`), t.nb > 20 ? ' — les 20 premières sont listées.' : '.'),
              t.exemples.length > 0
                ? h(
                    'ul',
                    { classe: 'exemples-regle' },
                    ...t.exemples.map((e) =>
                      h('li', {}, `${dateFr(e.date)} — ${e.tiers || e.libelle || '—'} — ${eurosCentimes(e.montant)}`),
                    ),
                  )
                : null,
              t.conflits.length > 0
                ? h(
                    'div',
                    {},
                    h(
                      'p',
                      { classe: 'retour-editeur ko' },
                      `${entier(t.conflits.length)} écriture(s) déjà arbitrée(s) vers un autre sous-poste : l’arbitrage prime, la règle ne les changera pas.`,
                    ),
                    h('ul', { classe: 'exemples-regle' }, ...t.conflits.map((c) => h('li', {}, c.libelle || c.transactionId))),
                  )
                : null,
            ),
          );
        } catch (e) {
          vider(zoneTest);
          echec(e);
        }
      })();
    };

    const enregistrer = (): void => {
      const r = saisie();
      if (!r) return;
      const cibleEdition = enEdition;
      void (async () => {
        try {
          await avecVerrou(verrouillables, () =>
            cibleEdition ? modifierRegle(cibleEdition.id, r) : creerRegle({ ...r, origine: 'manuelle' }),
          );
        } catch (e) {
          echec(e);
          return;
        }
        quitterEdition();
        await charger();
        await suivreReclassement(cibleEdition ? 'Règle modifiée.' : 'Règle enregistrée.');
      })();
    };

    const carte = h(
      'div',
      { classe: 'card' },
      titre,
      h(
        'div',
        { classe: 'editeur-champs' },
        h('label', {}, 'Champ', champ),
        h('label', {}, 'Opérateur', operateur),
        h('label', {}, 'Valeur', valeur),
        h('label', {}, 'Sens', sens),
        h('label', {}, 'Classer dans', cible),
      ),
      h('div', { classe: 'barre-actions' }, boutonTester, boutonEnregistrer, boutonAnnuler),
      zoneTest,
    );
    return carte;
  };

  void charger();

  const racine = h(
    'div',
    { classe: 'page', id: 'page-regles', role: 'region', 'aria-label': 'Règles de classement' },
    h(
      'section',
      {},
      enteteSection(1, 'Règles de classement', 'Critères appliqués avant la sollicitation de Jev'),
      noteLecture(
        'Les règles s’appliquent par priorité croissante : la première qui correspond classe l’écriture. ' +
          'Un arbitrage manuel prime toujours sur une règle.',
      ),
      etatPasse,
      retour,
      corps,
      admin ? formulaire() : null,
    ),
  );
  // Un reclassement lancé depuis un autre écran peut changer les nombres d'écritures.
  // Une vue retirée du document (changement de thème) se désabonne au premier appel.
  const desabonner = surFinDePasse(() => {
    if (!racine.isConnected) {
      desabonner();
      return;
    }
    void charger();
  });
  return racine;
}
